import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Check } from "lucide-react";

export default function Alerts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alerts");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const ack = async (id) => {
    try {
      await api.post(`/alerts/${id}/acknowledge`);
      toast.success("Acknowledged");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="page" data-testid="alerts-page">
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Inbox</div>
      <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Scope-change alerts</h1>

      <div className="mt-6">
        {loading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-[8px] p-10 text-center">
            <Check className="h-6 w-6 mx-auto text-emerald-600" />
            <div className="mt-2 mono text-xs uppercase tracking-widest text-muted-foreground">All clear</div>
            <p className="text-sm text-muted-foreground mt-1">No flagged scope changes right now.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((b) => (
              <div key={b.id} className="bg-card border border-rose-200 rounded-[8px] p-4" data-testid={`alert-${b.id}`}>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-rose-600" />
                  <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{b.booking_code}</span>
                </div>
                <div className="font-bold text-base mt-1">{b.customer_name} · {b.staff_name}</div>
                <div className="text-xs text-muted-foreground mono">{new Date(b.scheduled_start).toLocaleString()}</div>
                <div className="text-sm mt-2 bg-rose-50 border border-rose-200 rounded-[6px] p-2 whitespace-pre-wrap">{b.scope_change_note}</div>
                <div className="flex gap-2 mt-3">
                  <Link to={`/bookings/${b.id}/edit`}>
                    <Button variant="outline" size="sm" data-testid={`alert-open-${b.id}`}>
                      Open booking <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                  <Button size="sm" onClick={() => ack(b.id)} data-testid={`alert-ack-${b.id}`} className="btn-cta">
                    Acknowledge
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
